/**
 * AdminWiFi — operator surface for the Wi-Fi / Voucher / Captive Portal stack.
 *
 * Route: /admin/wifi (admin auth).
 *
 *   • Dashboard tiles (active sessions, vouchers, revenue).
 *   • Packages CRUD.
 *   • Voucher bulk generator + list with status filter + revoke.
 *   • Active sessions list with one-click disconnect.
 *
 * Talks only to /api/wifi/* — no router credentials ever touch the browser.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Wifi, Plus, Copy, X, Trash2, RefreshCw, Ticket, Package as PackageIcon,
  Activity, DollarSign, Server, Mail, MessageCircle, Smartphone, Send,
  Printer, FileDown, AlertTriangle, PackageOpen, CheckCircle2, Signal,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import AdminWiFiProviderSettings from './AdminWiFiProviderSettings';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// ============== Dashboard tiles ==============
const Tile = ({ label, value, icon: Icon, color = 'text-slate-300' }) => (
  <Card className="bg-slate-900/60 border-slate-800">
    <CardContent className="p-4 flex items-center gap-3">
      <Icon className={`h-7 w-7 ${color}`} />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <div className="text-xl font-semibold text-white tabular-nums">{value}</div>
      </div>
    </CardContent>
  </Card>
);

// ============== Packages tab ==============
const PackagesPanel = () => {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    name: '', duration_minutes: 60, data_mb: 500,
    speed_down_kbps: 2048, speed_up_kbps: 1024, price: 1.0, currency: 'USD',
    ssid: '', is_member_package: false,
  });
  const { data: settings } = useQuery({
    queryKey: ['wifi-settings'],
    queryFn: async () => (await axios.get(`${API}/api/wifi/settings`, { headers: authH() })).data,
  });
  const [net, setNet] = useState(null);
  const netForm = net || settings || { ssid: '', portal_url: '', welcome_message: '' };
  const saveNet = useMutation({
    mutationFn: async () => (await axios.put(`${API}/api/wifi/settings`, {
      ssid: netForm.ssid, portal_url: netForm.portal_url, welcome_message: netForm.welcome_message,
    }, { headers: authH() })).data,
    onSuccess: () => { toast.success('Network settings saved'); qc.invalidateQueries({ queryKey: ['wifi-settings'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  const { data: packages = [] } = useQuery({
    queryKey: ['wifi-packages'],
    queryFn: async () => (await axios.get(`${API}/api/wifi/packages`, { headers: authH() })).data,
  });
  const create = useMutation({
    mutationFn: async () => (await axios.post(`${API}/api/wifi/packages`, form, { headers: authH() })).data,
    onSuccess: () => { toast.success('Package created'); setAdding(false); qc.invalidateQueries({ queryKey: ['wifi-packages'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  const remove = useMutation({
    mutationFn: async (id) => axios.delete(`${API}/api/wifi/packages/${id}`, { headers: authH() }),
    onSuccess: () => { toast.success('Removed'); qc.invalidateQueries({ queryKey: ['wifi-packages'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Cannot delete'),
  });
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setAdding(true)} data-testid="wifi-package-add">
          <Plus className="h-4 w-4 mr-1" /> New package
        </Button>
      </div>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-slate-200 flex items-center gap-2">
            <Signal className="h-4 w-4 text-indigo-400" /> Network defaults
          </CardTitle>
        </CardHeader>
        <CardContent className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Network name (SSID)</Label>
            <Input value={netForm.ssid || ''} onChange={e => setNet({ ...netForm, ssid: e.target.value })}
                   className="bg-slate-800/60 border-slate-700" data-testid="wifi-settings-ssid" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Captive portal URL</Label>
            <Input value={netForm.portal_url || ''} onChange={e => setNet({ ...netForm, portal_url: e.target.value })}
                   className="bg-slate-800/60 border-slate-700" data-testid="wifi-settings-portal" />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Welcome message</Label>
            <Input value={netForm.welcome_message || ''} onChange={e => setNet({ ...netForm, welcome_message: e.target.value })}
                   className="bg-slate-800/60 border-slate-700" data-testid="wifi-settings-welcome" />
          </div>
          <div className="md:col-span-3 flex justify-end">
            <Button size="sm" onClick={() => saveNet.mutate()} disabled={saveNet.isPending}
                     data-testid="wifi-settings-save">
              {saveNet.isPending ? 'Saving…' : 'Save network defaults'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-right px-4 py-2">Duration</th>
                <th className="text-right px-4 py-2">Data</th>
                <th className="text-right px-4 py-2">Speed (↓/↑)</th>
                <th className="text-right px-4 py-2">Price</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {packages.map(p => (
                <tr key={p.id} className="border-b border-slate-800/50">
                  <td className="px-4 py-2 text-slate-100">
                    {p.name}{!p.is_active && <Badge variant="outline" className="ml-2 text-[10px]">disabled</Badge>}
                    {p.is_member_package && <Badge className="ml-2 text-[10px] bg-indigo-500/20 text-indigo-200 border-indigo-500/40" data-testid={`wifi-package-member-${p.id}`}>member access</Badge>}
                    {p.ssid && <span className="ml-2 text-[10px] text-slate-500">SSID {p.ssid}</span>}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-300 text-xs">
                    {p.duration_minutes ? `${p.duration_minutes} min` : '∞'}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-300 text-xs">
                    {p.data_mb ? `${p.data_mb} MB` : '∞'}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-300 text-xs tabular-nums">
                    {p.speed_down_kbps ? `${(p.speed_down_kbps/1024).toFixed(1)}` : '∞'} / {p.speed_up_kbps ? `${(p.speed_up_kbps/1024).toFixed(1)}` : '∞'} Mbps
                  </td>
                  <td className="px-4 py-2 text-right text-slate-100 tabular-nums">
                    {p.currency} {Number(p.price).toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-500 hover:text-rose-400"
                             onClick={() => window.confirm(`Delete "${p.name}"?`) && remove.mutate(p.id)}
                             data-testid={`wifi-package-delete-${p.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
              {packages.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-6">No packages yet.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={adding} onOpenChange={setAdding}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader><DialogTitle>New Wi-Fi package</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {[
              ['name', 'Name', 'text'],
              ['duration_minutes', 'Duration (min)', 'number'],
              ['data_mb', 'Data cap (MB)', 'number'],
              ['speed_down_kbps', 'Down (kbps)', 'number'],
              ['speed_up_kbps',   'Up (kbps)',   'number'],
              ['price',           'Price',       'number'],
              ['currency',        'Currency',    'text'],
              ['ssid',            'Network name (optional)', 'text'],
            ].map(([k, lbl, type]) => (
              <div key={k} className={k === 'name' ? 'col-span-2' : ''}>
                <Label className="text-xs text-slate-500">{lbl}</Label>
                <Input type={type} value={form[k]}
                       onChange={e => setForm({ ...form, [k]: type === 'number' ? Number(e.target.value) : e.target.value })}
                       className="bg-slate-800/60 border-slate-700"
                       data-testid={`wifi-package-new-${k}`} />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={form.is_member_package}
                   onChange={e => setForm({ ...form, is_member_package: e.target.checked })}
                   className="rounded" data-testid="wifi-package-new-member" />
            Use this package for members who sign in with their FWM account
          </label>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.name}
                     data-testid="wifi-package-save">
              {create.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Voucher delivery ==============
const CHANNEL_META = {
  email:    { label: 'Email',    icon: Mail },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle },
  sms:      { label: 'SMS',      icon: Smartphone },
};

const useChannelStatus = () => useQuery({
  queryKey: ['wifi-delivery-channels'],
  queryFn: async () => (await axios.get(`${API}/api/wifi/vouchers/delivery-channels`, { headers: authH() })).data,
  staleTime: 60000,
});

const ChannelPicker = ({ value, onChange, status }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-slate-500">Deliver the code via</Label>
    <div className="flex flex-wrap gap-2">
      {Object.entries(CHANNEL_META).map(([key, { label, icon: Icon }]) => {
        const on = value.includes(key);
        const st = status?.[key];
        const ready = st ? st.configured && st.enabled : true;
        return (
          <button key={key} type="button"
                  onClick={() => onChange(on ? value.filter(c => c !== key) : [...value, key])}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${on ? 'bg-indigo-500/20 border-indigo-400 text-indigo-100' : 'border-slate-700 text-slate-400 hover:text-slate-200'}`}
                  data-testid={`wifi-channel-${key}`}>
            <Icon className="h-3.5 w-3.5" />{label}
            {!ready && <span className="text-[9px] text-amber-400">not set up</span>}
          </button>
        );
      })}
    </div>
    {value.some(c => status?.[c] && !(status[c].configured && status[c].enabled)) && (
      <p className="text-[11px] text-amber-400 flex items-start gap-1" data-testid="wifi-channel-warning">
        <AlertTriangle className="h-3 w-3 mt-0.5" />
        Some selected channels are not configured yet — set them up in System Settings, or delivery will fail.
      </p>
    )}
  </div>
);

const DeliveryResult = ({ result, onRetry, retrying }) => {
  if (!result) return null;
  return (
    <div className="space-y-2" data-testid="wifi-delivery-result">
      {(result.delivered || []).map(c => (
        <div key={c} className="flex items-center gap-2 text-xs text-emerald-300">
          <CheckCircle2 className="h-3.5 w-3.5" /> Sent via {CHANNEL_META[c]?.label || c}
        </div>
      ))}
      {(result.failed || []).map(f => (
        <div key={f.channel} className="flex items-center justify-between gap-2 rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1.5"
             data-testid={`wifi-delivery-failed-${f.channel}`}>
          <span className="text-xs text-rose-200">
            <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
            {CHANNEL_META[f.channel]?.label || f.channel} failed: {f.error}
          </span>
          <Button size="sm" variant="outline" className="h-6 text-[11px]"
                  disabled={retrying} onClick={() => onRetry(f.channel)}
                  data-testid={`wifi-delivery-retry-${f.channel}`}>
            Retry
          </Button>
        </div>
      ))}
    </div>
  );
};

const downloadPdf = async ({ batchId, voucherIds, print = false }) => {
  const qs = batchId ? `batch_id=${batchId}` : `voucher_ids=${(voucherIds || []).join(',')}`;
  try {
    const res = await axios.get(`${API}/api/wifi/vouchers/pdf?${qs}`, { headers: authH(), responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    if (print) {
      window.open(url, '_blank');
    } else {
      const a = document.createElement('a');
      a.href = url; a.download = `wifi-vouchers-${batchId || 'selection'}.pdf`; a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (e) {
    toast.error('Could not build the voucher PDF');
  }
};

const SendVoucherDialog = ({ voucher, onClose }) => {
  const qc = useQueryClient();
  const { data: status } = useChannelStatus();
  const [form, setForm] = useState({
    guest_name: voucher.guest_name || '', guest_email: voucher.guest_email || '',
    guest_phone: voucher.guest_phone || '', channels: ['email'],
  });
  const [result, setResult] = useState(null);
  const send = useMutation({
    mutationFn: async (channels) => (await axios.post(
      `${API}/api/wifi/vouchers/${voucher.id}/deliver`,
      { ...form, channels: channels || form.channels }, { headers: authH() })).data,
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ['wifi-vouchers'] });
      if ((data.failed || []).length === 0) toast.success('Voucher delivered');
      else toast.error(`${data.failed.length} channel(s) failed — retry available`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Delivery failed'),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200" data-testid="wifi-send-dialog">
        <DialogHeader>
          <DialogTitle>Send voucher {voucher.code}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Assign this code to a guest and deliver it on the channels you choose.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-slate-500">Guest name</Label>
            <Input value={form.guest_name} onChange={e => setForm({ ...form, guest_name: e.target.value })}
                   className="bg-slate-800/60 border-slate-700" data-testid="wifi-send-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">Email</Label>
              <Input type="email" value={form.guest_email} onChange={e => setForm({ ...form, guest_email: e.target.value })}
                     className="bg-slate-800/60 border-slate-700" data-testid="wifi-send-email" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Phone (+27…)</Label>
              <Input value={form.guest_phone} onChange={e => setForm({ ...form, guest_phone: e.target.value })}
                     className="bg-slate-800/60 border-slate-700" data-testid="wifi-send-phone" />
            </div>
          </div>
          <ChannelPicker value={form.channels} status={status}
                         onChange={(channels) => setForm({ ...form, channels })} />
          <DeliveryResult result={result} retrying={send.isPending}
                          onRetry={(channel) => send.mutate([channel])} />
          {(voucher.deliveries || []).length > 0 && (
            <div className="rounded border border-slate-800 bg-slate-800/30 p-2 space-y-1"
                 data-testid="wifi-send-history">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Delivery log</div>
              {voucher.deliveries.slice(-6).reverse().map((d, i) => (
                <div key={i} className="text-[11px] text-slate-400">
                  {d.status === 'sent' ? '✅' : '⚠️'} {CHANNEL_META[d.channel]?.label || d.channel} → {d.to || '—'}
                  {' · '}{d.at ? new Date(d.at).toLocaleString() : ''}
                  {d.error ? ` · ${d.error}` : ''}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={() => send.mutate(null)}
                  disabled={send.isPending || form.channels.length === 0}
                  data-testid="wifi-send-submit">
            <Send className="h-4 w-4 mr-1" />{send.isPending ? 'Sending…' : 'Send voucher'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== Vouchers tab ==============
const VouchersPanel = ({ onGoToPackages }) => {
  const qc = useQueryClient();
  const [batchOpen, setBatchOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [batchForm, setBatchForm] = useState({
    package_id: '', quantity: 10, code_length: 10, valid_days: 30, multi_use: false,
    guest_name: '', guest_email: '', guest_phone: '', deliver_channels: [],
  });
  const [createdCodes, setCreatedCodes] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [sendVoucher, setSendVoucher] = useState(null);
  const { data: channelStatus } = useChannelStatus();

  const { data: packages = [] } = useQuery({
    queryKey: ['wifi-packages'],
    queryFn: async () => (await axios.get(`${API}/api/wifi/packages?active_only=true`, { headers: authH() })).data,
  });
  const { data: vouchers = [] } = useQuery({
    queryKey: ['wifi-vouchers', statusFilter],
    queryFn: async () => (await axios.get(
      `${API}/api/wifi/vouchers${statusFilter ? `?status=${statusFilter}` : ''}`,
      { headers: authH() }
    )).data,
  });

  const createBatch = useMutation({
    mutationFn: async () => (await axios.post(`${API}/api/wifi/vouchers/batch`, batchForm, { headers: authH() })).data,
    onSuccess: (data) => {
      setCreatedCodes(data.codes || []);
      setBatchResult(data);
      toast.success(`Created ${data.created} vouchers`);
      if (data.delivery?.failed?.length) {
        toast.error(`${data.delivery.failed.length} delivery channel(s) failed — retry from the dialog`);
      }
      qc.invalidateQueries({ queryKey: ['wifi-vouchers'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });

  const retryBatch = useMutation({
    mutationFn: async (channel) => (await axios.post(`${API}/api/wifi/vouchers/deliver-batch`, {
      batch_id: batchResult?.batch_id, channels: [channel],
      guest_name: batchForm.guest_name, guest_email: batchForm.guest_email, guest_phone: batchForm.guest_phone,
    }, { headers: authH() })).data,
    onSuccess: (data) => {
      setBatchResult((b) => ({ ...(b || {}), delivery: data }));
      if ((data.failed || []).length === 0) toast.success('Delivered on retry');
      else toast.error(`Retry failed: ${data.failed[0]?.error}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Retry failed'),
  });

  const revoke = useMutation({
    mutationFn: async (id) => axios.post(`${API}/api/wifi/vouchers/${id}/revoke`, { reason: 'admin' }, { headers: authH() }),
    onSuccess: () => { toast.success('Revoked'); qc.invalidateQueries({ queryKey: ['wifi-vouchers'] }); },
  });

  const STATUS_COLOR = { unused: 'bg-slate-500/20 text-slate-300', active: 'bg-emerald-500/20 text-emerald-300',
                          expired: 'bg-amber-500/20 text-amber-300', revoked: 'bg-rose-500/20 text-rose-300' };

  if (packages.length === 0) {
    return (
      <Card className="bg-slate-900/60 border-slate-800" data-testid="wifi-vouchers-empty-state">
        <CardContent className="py-14 text-center space-y-4">
          <PackageOpen className="h-12 w-12 mx-auto text-indigo-400/70" />
          <div className="space-y-1">
            <h3 className="text-base font-medium text-white">No Wi-Fi packages yet</h3>
            <p className="text-sm text-slate-400 max-w-md mx-auto">
              You need to create a Wi-Fi Package before generating vouchers. Go to the Packages tab to get started.
            </p>
          </div>
          <Button onClick={onGoToPackages} data-testid="wifi-vouchers-empty-cta">
            <PackageIcon className="h-4 w-4 mr-1" /> Go to Packages tab
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {sendVoucher && <SendVoucherDialog voucher={sendVoucher} onClose={() => setSendVoucher(null)} />}
      <div className="flex flex-wrap justify-between gap-2">
        <div className="flex gap-1">
          {['', 'unused', 'active', 'expired', 'revoked'].map(s => (
            <Button key={s || 'all'} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
                     onClick={() => setStatusFilter(s)}
                     data-testid={`wifi-voucher-filter-${s || 'all'}`}>
              {s || 'all'}
            </Button>
          ))}
        </div>
        <Button onClick={() => { setBatchOpen(true); setCreatedCodes([]); }} data-testid="wifi-voucher-batch">
          <Plus className="h-4 w-4 mr-1" /> Generate batch
        </Button>
      </div>
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Package</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2 hidden md:table-cell">Guest / delivery</th>
                <th className="text-right px-4 py-2 hidden md:table-cell">Created</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {vouchers.map(v => (
                <tr key={v.id} className="border-b border-slate-800/50">
                  <td className="px-4 py-2 font-mono text-indigo-200">{v.code}</td>
                  <td className="px-4 py-2 text-slate-300 text-xs">{v.package_name}</td>
                  <td className="px-4 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_COLOR[v.status] || ''}`}>{v.status}</span>
                  </td>
                  <td className="px-4 py-2 text-xs hidden md:table-cell">
                    <div className="text-slate-300">{v.guest_name || <span className="text-slate-600">unassigned</span>}</div>
                    <div className="flex gap-1 mt-0.5">
                      {(v.deliveries || []).reduce((acc, d) => {
                        if (!acc.find(x => x.channel === d.channel)) acc.push(d);
                        return acc;
                      }, []).map(d => (
                        <span key={d.channel}
                              className={`px-1.5 py-0.5 rounded text-[10px] ${d.status === 'sent' ? 'bg-emerald-500/15 text-emerald-300' : 'bg-rose-500/15 text-rose-300'}`}
                              title={d.error || `${d.channel} ${d.status}`}
                              data-testid={`wifi-voucher-chip-${v.id}-${d.channel}`}>
                          {CHANNEL_META[d.channel]?.label || d.channel}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right text-xs text-slate-500 hidden md:table-cell">
                    {v.created_at ? new Date(v.created_at).toLocaleString() : ''}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => setSendVoucher(v)}
                             className="text-indigo-300 hover:bg-indigo-500/10"
                             data-testid={`wifi-voucher-send-${v.id}`}>
                      <Send className="h-3.5 w-3.5 mr-1" /> Send
                    </Button>
                    {v.status === 'unused' || v.status === 'active' ? (
                      <Button variant="ghost" size="sm" onClick={() => revoke.mutate(v.id)}
                               className="text-rose-400 hover:bg-rose-500/10"
                               data-testid={`wifi-voucher-revoke-${v.id}`}>
                        Revoke
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {vouchers.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-6">No vouchers yet — generate a batch to get started.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
          <DialogHeader>
            <DialogTitle>Generate vouchers</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Codes are random + collision-checked.</DialogDescription>
          </DialogHeader>
          {createdCodes.length === 0 ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="text-xs text-slate-500">Package</Label>
                <select value={batchForm.package_id}
                        onChange={e => setBatchForm({ ...batchForm, package_id: e.target.value })}
                        className="w-full bg-slate-800/60 border border-slate-700 rounded-md p-2 text-sm"
                        data-testid="wifi-batch-package">
                  <option value="">— pick package —</option>
                  {packages.map(p => <option key={p.id} value={p.id}>{p.name} · {p.currency} {Number(p.price).toFixed(2)}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Quantity</Label>
                <Input type="number" min={1} max={5000} value={batchForm.quantity}
                       onChange={e => setBatchForm({ ...batchForm, quantity: Number(e.target.value) })}
                       className="bg-slate-800/60 border-slate-700" data-testid="wifi-batch-qty" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Code length</Label>
                <Input type="number" min={6} max={20} value={batchForm.code_length}
                       onChange={e => setBatchForm({ ...batchForm, code_length: Number(e.target.value) })}
                       className="bg-slate-800/60 border-slate-700" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Valid days</Label>
                <Input type="number" min={1} max={365} value={batchForm.valid_days}
                       onChange={e => setBatchForm({ ...batchForm, valid_days: Number(e.target.value) })}
                       className="bg-slate-800/60 border-slate-700" />
              </div>
              <div className="col-span-2 border-t border-slate-800 pt-3 space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Guest name</Label>
                    <Input value={batchForm.guest_name} onChange={e => setBatchForm({ ...batchForm, guest_name: e.target.value })}
                           className="bg-slate-800/60 border-slate-700" data-testid="wifi-batch-guest-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input type="email" value={batchForm.guest_email} onChange={e => setBatchForm({ ...batchForm, guest_email: e.target.value })}
                           className="bg-slate-800/60 border-slate-700" data-testid="wifi-batch-guest-email" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Phone</Label>
                    <Input value={batchForm.guest_phone} onChange={e => setBatchForm({ ...batchForm, guest_phone: e.target.value })}
                           className="bg-slate-800/60 border-slate-700" data-testid="wifi-batch-guest-phone" />
                  </div>
                </div>
                <ChannelPicker value={batchForm.deliver_channels} status={channelStatus}
                               onChange={(deliver_channels) => setBatchForm({ ...batchForm, deliver_channels })} />
                <p className="text-[11px] text-slate-500">
                  Leave the channels blank to just print the batch — you can still send any code individually later.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-slate-800/40 border border-slate-700 rounded p-3 font-mono text-xs text-emerald-200 max-h-56 overflow-y-auto"
                   data-testid="wifi-batch-codes">
                {createdCodes.map(c => <div key={c}>{c}</div>)}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm"
                         onClick={() => { navigator.clipboard.writeText(createdCodes.join('\n')); toast.success('Copied'); }}>
                  <Copy className="h-3.5 w-3.5 mr-1" /> Copy all
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf({ batchId: batchResult?.batch_id })}
                         data-testid="wifi-batch-pdf">
                  <FileDown className="h-3.5 w-3.5 mr-1" /> Download PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => downloadPdf({ batchId: batchResult?.batch_id, print: true })}
                         data-testid="wifi-batch-print">
                  <Printer className="h-3.5 w-3.5 mr-1" /> Print slips
                </Button>
              </div>
              <DeliveryResult result={batchResult?.delivery} retrying={retryBatch.isPending}
                              onRetry={(channel) => retryBatch.mutate(channel)} />
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setBatchOpen(false)}>Close</Button>
            {createdCodes.length === 0 && (
              <Button onClick={() => createBatch.mutate()}
                       disabled={createBatch.isPending || !batchForm.package_id}
                       data-testid="wifi-batch-create">
                {createBatch.isPending ? 'Creating…' : 'Generate'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============== Sessions tab ==============
const SessionsPanel = () => {
  const qc = useQueryClient();
  const { data: sessions = [] } = useQuery({
    queryKey: ['wifi-sessions'],
    queryFn: async () => (await axios.get(`${API}/api/wifi/sessions?status=active`, { headers: authH() })).data,
    refetchInterval: 10000,
  });
  const disc = useMutation({
    mutationFn: async (id) => axios.post(`${API}/api/wifi/sessions/${id}/disconnect`, {}, { headers: authH() }),
    onSuccess: () => { toast.success('Disconnected'); qc.invalidateQueries({ queryKey: ['wifi-sessions'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Failed'),
  });
  return (
    <Card className="bg-slate-900/60 border-slate-800">
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-slate-500 border-b border-slate-800">
            <tr>
              <th className="text-left px-4 py-2">Device</th>
              <th className="text-left px-4 py-2">Package</th>
              <th className="text-left px-4 py-2">Voucher</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Started</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Expires</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody data-testid="wifi-sessions-table">
            {sessions.map(s => (
              <tr key={s.id} className="border-b border-slate-800/50">
                <td className="px-4 py-2 text-slate-100">{s.device_name || s.device_mac || '—'}</td>
                <td className="px-4 py-2 text-slate-300 text-xs">{s.package_name}</td>
                <td className="px-4 py-2 font-mono text-indigo-200 text-xs">{s.voucher_code}</td>
                <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell">{new Date(s.started_at).toLocaleString()}</td>
                <td className="px-4 py-2 text-xs text-slate-500 hidden md:table-cell">
                  {s.expires_at ? new Date(s.expires_at).toLocaleString() : '∞'}
                </td>
                <td className="px-4 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => disc.mutate(s.id)}
                           className="text-rose-400 hover:bg-rose-500/10"
                           data-testid={`wifi-session-disconnect-${s.id}`}>
                    <X className="h-3.5 w-3.5 mr-1" /> Kick
                  </Button>
                </td>
              </tr>
            ))}
            {sessions.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-6">No active sessions.</td></tr>}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
};

// ============== Top-level ==============
const AdminWiFi = () => {
  const [tab, setTab] = useState('vouchers');
  const { data: dash, refetch } = useQuery({
    queryKey: ['wifi-dashboard'],
    queryFn: async () => (await axios.get(`${API}/api/wifi/dashboard`, { headers: authH() })).data,
    refetchInterval: 15000,
  });
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-white inline-flex items-center gap-2">
              <Wifi className="h-6 w-6 text-indigo-400" /> Wi-Fi & Vouchers
            </h1>
            <p className="text-sm text-slate-400 mt-1">
              Manage internet packages, generate voucher codes, control live captive-portal sessions.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </header>

        {/* Dashboard tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Tile label="Active sessions" value={dash?.active_sessions ?? '—'}     icon={Activity}    color="text-emerald-300" />
          <Tile label="Today redeemed"  value={dash?.today_redemptions ?? '—'}   icon={Ticket}      color="text-indigo-300" />
          <Tile label="Unused vouchers" value={dash?.vouchers_unused ?? '—'}     icon={PackageIcon} color="text-slate-300" />
          <Tile label="Revenue today"   value={`$${Number(dash?.revenue_today_usd || 0).toFixed(2)}`} icon={DollarSign} color="text-amber-300" />
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-slate-900/60 border border-slate-800">
            <TabsTrigger value="vouchers" data-testid="wifi-tab-vouchers">Vouchers</TabsTrigger>
            <TabsTrigger value="packages" data-testid="wifi-tab-packages">Packages</TabsTrigger>
            <TabsTrigger value="sessions" data-testid="wifi-tab-sessions">Live sessions</TabsTrigger>
            <TabsTrigger value="provider" data-testid="wifi-tab-provider" className="gap-1.5"><Server className="h-3.5 w-3.5" />Provider</TabsTrigger>
          </TabsList>
          <TabsContent value="vouchers" className="mt-4"><VouchersPanel onGoToPackages={() => setTab('packages')} /></TabsContent>
          <TabsContent value="packages" className="mt-4"><PackagesPanel /></TabsContent>
          <TabsContent value="sessions" className="mt-4"><SessionsPanel /></TabsContent>
          <TabsContent value="provider" className="mt-4"><AdminWiFiProviderSettings /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminWiFi;
